/* eslint-disable react/require-default-props */
import {
  Box,
  BoxProps,
  Checkbox,
  Chip,
  colors,
  Grow,
  Paper,
  styled,
  TextField,
} from '@material-ui/core';
import { RadioButtonChecked } from '@material-ui/icons';
import {
  AutocompleteGetTagProps,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  Value,
} from '@material-ui/lab';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import { HTMLAttributes, ReactNode, useCallback } from 'react';
import { AutocompleteStyled } from './AutocompleteStyled';

export type DynamicAutocompleteType<
  T,
  Multiple extends boolean = boolean,
  DisableClearable extends boolean = boolean,
  FreeSolo extends boolean = boolean
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'renderInput' | 'onChange'
> & {
  clearable?: DisableClearable extends true ? false : true | undefined;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean | undefined;
  helperText?: string;
  getOptionStatus?: (option: T) => boolean | undefined;
  getOptionLabel: (option: T) => string | undefined;
  renderInput?: (
    params: AutocompleteRenderInputParams
  ) => ReactNode | undefined;
  sx?: BoxProps['sx'];
  onChange?: (
    // eslint-disable-next-line @typescript-eslint/ban-types
    event: React.ChangeEvent<{}> | null,
    value: Value<T, Multiple, DisableClearable, FreeSolo>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
};

function DynamicAutocomplete<
  T,
  Multiple extends boolean = boolean,
  DisableClearable extends boolean = boolean,
  FreeSolo extends boolean = boolean
>({
  className,
  label,
  placeholder,
  disabled,
  multiple,
  value,
  onChange,
  onBlur,
  getOptionLabel,
  fullWidth,
  error,
  helperText,
  renderTags,
  options,
  renderInput,
  renderOption,
  getOptionStatus,
  size = 'small',
  sx,
  clearable,
  ...autocompleteProps
}: DynamicAutocompleteType<
  T,
  Multiple,
  DisableClearable,
  FreeSolo
>): JSX.Element {
  const renderStatus = useCallback(
    (option: T) =>
      getOptionStatus &&
      typeof getOptionStatus(option) === 'boolean' && (
        <DotStatus isActive={getOptionStatus(option)} />
      ),
    [getOptionStatus]
  );

  const renderOptionDefault = useCallback(
    (option: T, state: AutocompleteRenderOptionState) => {
      const labelOption = getOptionLabel(option);
      return (
        <>
          <ListItemStyled>
            {multiple ? (
              <>
                <Checkbox checked={state.selected} edge="start" />
                {labelOption}
              </>
            ) : (
              <>
                {renderStatus(option)}
                {labelOption}
              </>
            )}
          </ListItemStyled>
        </>
      );
    },
    [getOptionLabel, multiple, renderStatus]
  );

  const renderTagsDefault = useCallback(
    (value_: T[], getTagProps: AutocompleteGetTagProps) =>
      value_.map((option, index) => {
        const labelChip = getOptionLabel(option);
        return (
          <Chip
            key={labelChip}
            size="small"
            variant="default"
            label={labelChip}
            style={{ marginBottom: -1 }}
            {...getTagProps({ index })}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getOptionLabel]
  );

  const renderInputDefault = useCallback(
    ({ InputProps, ...params }: AutocompleteRenderInputParams) => {
      const optionInput = Array.isArray(value)
        ? undefined
        : options.find(
            option => getOptionLabel(option) === getOptionLabel(value as T)
          );
      return (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          InputProps={{
            ...InputProps,
            startAdornment: optionInput
              ? renderStatus(optionInput)
              : InputProps.startAdornment,
            placeholder,
          }}
          InputLabelProps={{ margin: 'dense' }}
          error={error}
          helperText={helperText}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      value,
      options,
      label,
      placeholder,
      renderStatus,
      error,
      helperText,
      getOptionLabel,
    ]
  );

  return (
    <Box className={className} sx={sx}>
      <AutocompleteStyled
        data-testid="autocomplete"
        className="department-select"
        multiple={multiple}
        autoComplete
        autoHighlight
        fullWidth={fullWidth}
        disableCloseOnSelect={multiple}
        size={size}
        options={options}
        renderTags={renderTags || renderTagsDefault}
        getOptionLabel={getOptionLabel}
        renderInput={renderInput || renderInputDefault}
        renderOption={renderOption || renderOptionDefault}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        PaperComponent={PaperComponent}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        disableClearable={!clearable as any}
        {...autocompleteProps}
      />
    </Box>
  );
}

const PaperComponent: React.FC<HTMLAttributes<HTMLElement>> = props => (
  <Grow in timeout={150}>
    <Paper {...props} />
  </Grow>
);

export const DotStatus: React.FC<{ isActive: boolean | undefined }> = ({
  isActive,
}) => (
  <RadioButtonChecked
    data-testid="status-indicator"
    style={{
      color: isActive ? colors.green[300] : colors.red[400],
      fontSize: 16,
    }}
  />
);

const ListItemStyled = styled('span')({
  display: 'flex',
  gap: 8,
  flexWrap: 'nowrap',
  alignItems: 'center',
});

export default DynamicAutocomplete;
